import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { VbzButton, VbzModal } from "@smiles/voebiz-ui-kit";
import { ModalBody } from "reactstrap";
import moment from "moment";
import { useFlags } from "launchdarkly-react-client-sdk";

import { useTransactions } from "../../context/TransactionsContext";

import { FilterRadio } from "..";

import { getTransactions } from "../../services/api";

import { isGolPremiumEnabled } from "../../utils/statementFare";
import { FiltersItems, Sections } from "./types";

const FilterModal: FC = () => {
  const flags = useFlags();
  const { apiDomain } = flags;
  const golPremiumEnabled = isGolPremiumEnabled(flags);
  const { t } = useTranslation("filter");
  const { dispatch, state } = useTransactions();
  const [options, setOptions] = useState({
    period: state.filter.periodFilter,
    transaction: state.filter.transactionFilter,
    points: state.filter.pointsFilter,
  });
  const [isLoading, setIsLoading] = useState(false);
  const toggle = () =>
    dispatch({
      type: "MODAL",
      modals: {
        filterModalOpen: !state.modals.filterModalOpen,
        threeYearsModalOpen: false,
        legacyUserModalOpen: false,
      },
    });

  const filters: FiltersItems = {
    period: {
      title: t("byPeriod"),
      items: [
        { id: "LAST_7_DAYS", label: t("last7Days") },
        { id: "LAST_30_DAYS", label: t("last30Days") },
        { id: "THIS_YEAR", label: t("thisYear") },
        { id: "LAST_YEAR", label: t("lastYear") },
      ],
    },
    transaction: {
      title: t("byTransaction"),
      items: [
        { id: "ACCRUAL", label: t("accumulations") },
        { id: "REDEMPTION", label: t("rescue") },
      ],
    },
    points: {
      title: t("byPoints"),
      items: [
        { id: "REDEEMABLE", label: t("voebizPoints") },
        { id: "QUALIFIABLE", label: t("qualifiedPoints") },
      ],
    },
  };

  const renderFilters = () => {
    return Object.keys(filters).map((section) => {
      const element = filters[section as Sections];

      return (
        <div className="filter-modal__section" key={element.title}>
          <p className="filter-modal-title">{element.title}</p>

          {element.items.map((item) => (
            <FilterRadio
              className="filter-modal__section-radio"
              key={item.id}
              id={item.id}
              label={item.label}
              name={item.id}
              value={item.label}
              checked={item.id === options[section as Sections]}
              onChange={() => setOptions((prevState) => ({ ...prevState, [section as Sections]: item.id }))}
            />
          ))}
        </div>
      );
    });
  };

  const submitForm = React.useCallback(async () => {
    setIsLoading(true);
    const dates = {
      LAST_7_DAYS: { startDate: moment().subtract("7", "d"), endDate: moment() },
      LAST_30_DAYS: { startDate: moment().subtract("30", "d"), endDate: moment() },
      THIS_YEAR: { startDate: moment().startOf("year"), endDate: moment() },
      LAST_YEAR: {
        startDate: moment().subtract("1", "year"),
        endDate: moment().subtract("1", "year").endOf("year"),
      },
    };

    const { startDate, endDate } = dates[options.period];

    try {
      const transactions = await getTransactions({
        apiDomain,
        jwt: state.tokens.jwt,
        startDate,
        endDate,
        mileTypes: options.points as MileT,
        transactionType: options.transaction as TransactionT,
        golPremiumEnabled,
      });
      toggle();
      dispatch({
        type: "FILTER",
        filter: {
          periodFilter: options.period,
          pointsFilter: options.points,
          transactionFilter: options.transaction,
          startDate: startDate.format("DD/MM/YYYY"),
          endDate: endDate.format("DD/MM/YYYY"),
        },
        transactions,
      });
      setIsLoading(false);
      if (transactions[0]?.isLegacy) {
        dispatch({
          type: "MODAL",
          modals: { filterModalOpen: false, threeYearsModalOpen: false, legacyUserModalOpen: true },
        });
      }
    } catch (err) {
      setIsLoading(false);
      toggle();
      dispatch({ type: "ERROR" });
    }
  }, [options.period, options.points, options.transaction, golPremiumEnabled]);

  return (
    <VbzModal isOpen={state.modals.filterModalOpen} className="filter-modal" toggle={toggle}>
      <ModalBody>
        <h4 className="filter-modal__title">{t("filterYourSearch")}</h4>
        <form>
          {renderFilters()}

          <VbzButton isLoading={isLoading} className="filter-modal__btn" text="Aplicar filtro" onClick={submitForm} />
        </form>
      </ModalBody>
    </VbzModal>
  );
};

export default FilterModal;
